import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Avatar,
  Button,
  CssBaseline,
  TextField,
  Link,
  Paper,
  Box,
  Grid,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  LockReset as LockResetIcon,
  AdminPanelSettings,
} from "@mui/icons-material";
import { indigo } from "@mui/material/colors";
import { Link as RouterLink } from "react-router-dom";
import adminAuthBg from "../../images/bg-img.png";
import useAdminContext from "../../store/contexts/AdminContext";
import Copyright from "../../components/Copyright/Copyright";
import { ADMIN_ROUTES } from "../../routes/Admin/AdminPaths";

export default function AdminForgotPassword() {
  const {
    authState: { loading, error, message },
    forgotPasswordAdminApiCall,
    clearErrors,
  } = useAdminContext();

  const history = useHistory();
  const [email, setEmail] = useState("");
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Clear any previous errors when component mounts
    clearErrors();
  }, []); // Remove clearErrors from dependency array

  useEffect(() => {
    // Show success message if API returns success
    if (message && !error) {
      setSuccess(true);
    }
  }, [message, error]);

  const validateForm = () => {
    if (!email.trim()) {
      setFormError("Email is required");
      return false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setFormError("Email is invalid");
      return false;
    }

    setFormError("");
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (validateForm()) {
      await forgotPasswordAdminApiCall({ email });
    }
  };

  const handleBackToLogin = () => {
    // Clear states before navigating
    setSuccess(false);
    setEmail("");
    setFormError("");
    clearErrors();
  };

  return (
    <Grid container component="main" sx={{ height: "100vh" }}>
      <CssBaseline />
      <Grid
        item
        xs={false}
        sm={4}
        md={7}
        sx={{
          backgroundImage: `url(${adminAuthBg})`,
          backgroundRepeat: "no-repeat",
          backgroundColor: (t) =>
            t.palette.mode === "light"
              ? t.palette.grey[50]
              : t.palette.grey[900],
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
        <Box
          sx={{
            my: 8,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: indigo[700], width: 56, height: 56 }}>
            <LockResetIcon fontSize="large" />
          </Avatar>
          <Typography
            component="h1"
            variant="h5"
            sx={{ mt: 2, fontWeight: 500 }}
          >
            Reset Admin Password
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mt: 1, mb: 2 }}
          >
            Enter your email address and we'll send you a link to reset your
            password.
          </Typography>

          {error && !success && (
            <Alert severity="error" sx={{ mt: 2, width: "100%" }}>
              {error}
            </Alert>
          )}

          {success && message ? (
            <Box sx={{ mt: 3, textAlign: "center", width: "100%" }}>
              <Alert severity="success" sx={{ mb: 3 }}>
                {message}
              </Alert>
              <Typography variant="body2" sx={{ mb: 3 }}>
                Please check your email for instructions to reset your password.
              </Typography>
              <Button
                component={RouterLink}
                to={ADMIN_ROUTES.login}
                variant="outlined"
                color="primary"
                onClick={handleBackToLogin}
                sx={{ mt: 2 }}
              >
                Back to Login
              </Button>
            </Box>
          ) : (
            <Box
              component="form"
              noValidate
              onSubmit={handleSubmit}
              sx={{ mt: 3, width: "100%" }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  // Clear form error when user starts typing
                  if (formError) setFormError("");
                }}
                error={!!formError}
                helperText={formError}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.2,
                  backgroundColor: indigo[700],
                  "&:hover": {
                    backgroundColor: indigo[800],
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "Send Reset Link"
                )}
              </Button>

              <Grid container justifyContent="center">
                <Grid item>
                  <Link
                    component={RouterLink}
                    to={ADMIN_ROUTES.login}
                    variant="body2"
                    color="primary"
                    onClick={handleBackToLogin}
                  >
                    Remember your password? Sign in
                  </Link>
                </Grid>
              </Grid>

              <Copyright sx={{ mt: 5 }} />
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
}
